<script setup lang="ts">
import SearchMap from "@/components/SearchMap/SearchMap.vue"
import { useFilters } from "@/helpers/filter.helper";
import { getSafeNumber } from "@/helpers/number.helper";
import { SearchEntity } from "@/models/search.model";
import CmsDataProvider, { CMS_DATA_ID } from "@/providers/cms.provider";
import activiteitRepository, { Activiteit, IActiviteitSearchParams, MarkerData } from "@/repositories/activiteit.repository";
import sportaanbiederRepository, { ISportaanbiederSearchParams } from "@/repositories/sportaanbieder.repository";
import { usePlatformStore } from "@/stores/platform.store";
import RedirectPlatformModal from "@/components/Modals/RedirectPlatformModal.vue"
import { notify } from "@kyvg/vue3-notification";
import { LngLat, LngLatBounds } from "mapbox-gl";
import { computed, onBeforeMount, reactive } from "vue";

const platformStore = usePlatformStore();

const cmsData = CmsDataProvider.get<{
    mapCentre: { latitude: number | string, longitude: number | string },
    mapMinBound: { latitude: number | string, longitude: number | string },
    mapMaxBound: { latitude: number | string, longitude: number | string },
    mapZoom: number,
    activitiesSingular: string,
    activitiesPlural: string,
    displayActivities: boolean,
    displayClubs: boolean
}>(CMS_DATA_ID.ACTIVITEITEN_MAP);

const state = reactive<{
    selectedActivity?: Activiteit,
    location?: LngLat,
    activities: SearchEntity<MarkerData>[],
    clubs: SearchEntity<MarkerData>[],
}>({
    activities: [],
    clubs: []
});

const searchFilter = useFilters<IActiviteitSearchParams>({
    type: 'sportaanbod',
    platform_facet: platformStore.platformId,
    facetlist: ["sporten_facet", "cultuuractiviteiten_facet", "extrakosten_facet", "plaatsen_facet", "sporttypen_facet", "activiteittypen_facet", "aanbod_facet", "betalingstype_facet", "doelgroep_facet", "specifiekaanbod_facet", "fysiekeinspanning_facet", "golfstarttype_facet"],
    pageSize: 20,
    offset: 0,
    OrderBy: 'StartDatum',
}, () => {
    loadActivityMarkers();
});

const zoom = cmsData.mapZoom ?? 7;
const maxZoom = 17;
const center = new LngLat(
    getSafeNumber(cmsData.mapCentre.longitude) ?? 5.2793703,
    getSafeNumber(cmsData.mapCentre.latitude) ?? 52.2129919
);
const bounds = new LngLatBounds(
    new LngLat(
        getSafeNumber(cmsData.mapMinBound.longitude) ?? 1.31497114423,
        getSafeNumber(cmsData.mapMinBound.latitude) ?? 49.803721015
    ),
    new LngLat(
        getSafeNumber(cmsData.mapMaxBound.longitude) ?? 9.09205325687,
        getSafeNumber(cmsData.mapMaxBound.latitude) ?? 54.5104033474
    ),
)

const activitiesSingular = computed(() => {
    return cmsData.activitiesSingular ? cmsData.activitiesSingular : "activiteit"
})

const activitiesPlural = computed(() => {
    return cmsData.activitiesPlural ? cmsData.activitiesPlural : "activiteiten"
})

const markers = computed(() => {
    return [...new Set([...state.activities, ...state.clubs])]
})

const loadActivityMarkers = () => {
    if (!cmsData.displayActivities) {
        return;
    }

    activiteitRepository.getMarkers(searchFilter.filters, false)
        .then(markerData => state.activities = markerData.Items)
        .catch(() => {
            notify({
                type: 'error',
                title: 'Er is een fout opgetreden bij het ophalen de data voor de kaart',
                duration: 20000
            });
        });
}

const loadClubMarkers = () => {
    if (!cmsData.displayClubs) {
        return;
    }

    sportaanbiederRepository.getMarkers({
        platform_facet: !platformStore.isGlobalPlatform ? platformStore.platformId : undefined
    })
        .then(markerData => markerData.Items.map(item => ({
            Type: "SportaanbiederLocatie",
            Entity: {                
                SportmomentId: null,
                SportaanbiederId: item.Entity.SportaanbiederId,
                Latitude: item.Entity.LocatieItem.Latitude,
                Longitude: item.Entity.LocatieItem.Longitude
            }
        })))
        .then(clubs => state.clubs = clubs)
        .catch(() => {
            notify({
                type: 'error',
                title: 'Er is een fout opgetreden bij het ophalen de data voor de kaart',
                duration: 20000
            });
        });
}

const startRedirect = (entity: Activiteit) => {
    state.selectedActivity = entity;
}

const cancelRedirect = () => {
    state.selectedActivity = undefined;
}

onBeforeMount(async () => {
    await platformStore.ensurePlatform();
    loadActivityMarkers();
    loadClubMarkers();
})
</script>

<template>
    <SearchMap :initial-zoom="zoom" :initial-center="center" :bounds="bounds" :max-zoom="maxZoom"
        :activity-plural="activitiesPlural" :activity-singular="activitiesSingular" :markers="markers"
        :location="state.location" @selected="startRedirect">
    </SearchMap>

    <RedirectPlatformModal v-if="state.selectedActivity !== undefined"
        :activityId="state.selectedActivity.SportmomentId" :platformUrl="state.selectedActivity.PlatformItem.Link"
        @cancel="cancelRedirect" />
</template>
